// components/ChatHistoryList.js
// Claude-style chat history (New chat, Outreach, search, "Recents" list with rename/delete),
// embedded inside the single sidebar (SlidingSlidebar). Reads/writes through
// ConversationContext (MongoDB-backed). Styling is class-based for consistent hover/active
// and pixel-aligned text positions, matching Claude's sidebar.
import React, { useState, useEffect, useMemo } from 'react';
import {
  SquarePen, Trash2, Pencil, MoreHorizontal, Check, X as XIcon, Search, Mail, Scale
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useConversations } from '../context/ConversationContext';
import { UserAuth } from '../context/AuthContext';

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, "Helvetica Neue", Arial, sans-serif';

const CSS = `
.cs-sb { font-family: ${FONT}; color: #3d3d3a; }
.cs-sb, .cs-sb * { box-sizing: border-box; font-family: ${FONT}; }
.cs-sb .cs-item {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px; margin: 0; border: none; background: transparent;
  border-radius: 8px; cursor: pointer; text-align: left;
  font-size: 14px; font-weight: 500; line-height: 1.25; color: #3d3d3a;
}
.cs-sb .cs-item:hover { background: #ecebe3; }
.cs-sb .cs-item svg { flex-shrink: 0; }
.cs-sb .cs-search {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px; margin: 2px 0 4px; border-radius: 8px; background: transparent;
}
.cs-sb .cs-search:focus-within { background: #ecebe3; }
.cs-sb .cs-search input {
  flex: 1; min-width: 0; border: none; outline: none; background: transparent;
  font-size: 14px; color: #3d3d3a;
}
.cs-sb .cs-search input::placeholder { color: #9a9a90; }
.cs-sb .cs-label {
  font-size: 12px; font-weight: 600; color: #91918a;
  padding: 14px 10px 4px; letter-spacing: 0.01em;
}
.cs-sb .cs-row { display: flex; align-items: center; border-radius: 8px; }
.cs-sb .cs-row:hover { background: #ecebe3; }
.cs-sb .cs-row.cs-active { background: #e6e4da; }
.cs-sb .cs-row-title {
  flex: 1; min-width: 0; background: transparent; border: none; cursor: pointer;
  padding: 8px 10px; text-align: left;
}
.cs-sb .cs-row-title span {
  display: block; font-size: 14px; font-weight: 500; line-height: 1.25; color: #3d3d3a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cs-sb .cs-iconbtn {
  background: transparent; border: none; cursor: pointer; padding: 5px;
  border-radius: 6px; display: flex; align-items: center; color: #73736b;
  transition: opacity .12s, background .12s;
}
.cs-sb .cs-iconbtn:hover { background: #deded4; }
.cs-sb .cs-menu-btn { opacity: 0; margin-right: 4px; }
.cs-sb .cs-row:hover .cs-menu-btn, .cs-sb .cs-menu-btn.cs-open { opacity: 1; }
.cs-sb .cs-menu {
  position: absolute; right: 0; top: 30px; background: #fff; border: 1px solid #e6e6e1;
  border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.13); padding: 5px; z-index: 50; min-width: 150px;
}
.cs-sb .cs-menuitem {
  display: flex; align-items: center; gap: 9px; width: 100%; background: transparent;
  border: none; cursor: pointer; padding: 8px 10px; font-size: 13.5px; color: #3d3d3a;
  border-radius: 7px; text-align: left;
}
.cs-sb .cs-menuitem:hover { background: #f0efe8; }
.cs-sb .cs-edit { display: flex; align-items: center; gap: 4px; width: 100%; padding: 2px 6px; }
.cs-sb .cs-edit input {
  flex: 1; min-width: 0; font-size: 14px; padding: 6px 7px; border: 1px solid #c8c8c0;
  border-radius: 7px; outline: none; font-family: ${FONT};
}
.cs-sb .cs-muted { font-size: 13px; color: #9a9a90; padding: 10px; }
`;

const BUCKET_ORDER = ['Today', 'Yesterday', 'Previous 7 Days', 'Previous 30 Days', 'Older'];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function bucketFor(dateStr) {
  if (!dateStr) return 'Older';
  const now = startOfDay(new Date());
  const then = startOfDay(new Date(dateStr));
  const days = Math.round((now - then) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'Previous 7 Days';
  if (days <= 30) return 'Previous 30 Days';
  return 'Older';
}

// onAfterAction: called after picking a chat / new chat / Outreach so the drawer can close.
export default function ChatHistoryList({ onAfterAction }) {
  const {
    conversations, currentId, loadingList,
    newChat, selectConversation, renameConversation, deleteConversation
  } = useConversations() || {};
  const { user } = UserAuth() || {};
  const navigate = useNavigate();

  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!menuOpenId) return undefined;
    const close = () => setMenuOpenId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpenId]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (conversations || [])
      .filter((c) => (q ? (c.title || '').toLowerCase().includes(q) : true))
      .slice()
      .sort((a, b) => new Date(b.lastMessageAt || b.createdAt || 0) - new Date(a.lastMessageAt || a.createdAt || 0));
    const groups = {};
    for (const c of list) {
      const b = bucketFor(c.lastMessageAt || c.createdAt);
      (groups[b] = groups[b] || []).push(c);
    }
    return groups;
  }, [conversations, search]);

  const hasResults = BUCKET_ORDER.some((b) => grouped[b]?.length);

  if (!user) return null;

  const startRename = (c) => { setMenuOpenId(null); setEditingId(c._id); setEditValue(c.title || ''); };
  const commitRename = (id) => {
    const v = editValue.trim();
    if (v) renameConversation?.(id, v);
    setEditingId(null); setEditValue('');
  };
  const confirmDelete = (c) => {
    setMenuOpenId(null);
    if (window.confirm(`Delete "${c.title || 'this chat'}"? This cannot be undone.`)) deleteConversation?.(c._id);
  };

  return (
    <div className="cs-sb">
      <style>{CSS}</style>

      <button className="cs-item" onClick={() => { newChat?.(); onAfterAction?.(); }}>
        <SquarePen size={17} color="#c2613f" />
        <span>New chat</span>
      </button>

      <button className="cs-item" onClick={() => { navigate('/outreach'); onAfterAction?.(); }}>
        <Mail size={17} color="#73736b" />
        <span>Outreach</span>
      </button>

      <button className="cs-item" onClick={() => { navigate('/legal'); onAfterAction?.(); }}>
        <Scale size={17} color="#73736b" />
        <span>Legal AI</span>
      </button>

      {(conversations || []).length > 0 && (
        <div className="cs-search">
          <Search size={16} color="#9a9a90" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search chats" />
          {search && (
            <button className="cs-iconbtn" title="Clear" onClick={() => setSearch('')}><XIcon size={14} /></button>
          )}
        </div>
      )}

      <div>
        {loadingList && <div className="cs-muted">Loading…</div>}
        {!loadingList && (conversations || []).length === 0 && (
          <div className="cs-muted">No chats yet. Start a new search.</div>
        )}
        {!loadingList && (conversations || []).length > 0 && !hasResults && (
          <div className="cs-muted">No chats match “{search}”.</div>
        )}

        {BUCKET_ORDER.filter((b) => grouped[b]?.length).map((bucket) => (
          <div key={bucket}>
            <div className="cs-label">{bucket}</div>
            {grouped[bucket].map((c) => {
              const active = c._id === currentId;
              return (
                <div key={c._id} className={`cs-row${active ? ' cs-active' : ''}`}>
                  {editingId === c._id ? (
                    <div className="cs-edit">
                      <input
                        autoFocus value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(c._id);
                          if (e.key === 'Escape') { setEditingId(null); setEditValue(''); }
                        }}
                      />
                      <button className="cs-iconbtn" onMouseDown={(e) => { e.preventDefault(); commitRename(c._id); }} title="Save"><Check size={14} /></button>
                      <button className="cs-iconbtn" onMouseDown={(e) => { e.preventDefault(); setEditingId(null); }} title="Cancel"><XIcon size={14} /></button>
                    </div>
                  ) : (
                    <>
                      <button className="cs-row-title" title={c.title} onClick={() => { selectConversation?.(c._id); onAfterAction?.(); }}>
                        <span>{c.title || 'New chat'}</span>
                      </button>
                      <div style={{ position: 'relative' }}>
                        <button
                          className={`cs-iconbtn cs-menu-btn${menuOpenId === c._id ? ' cs-open' : ''}`}
                          title="Options"
                          onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === c._id ? null : c._id); }}
                        >
                          <MoreHorizontal size={16} />
                        </button>
                        {menuOpenId === c._id && (
                          <div className="cs-menu" onClick={(e) => e.stopPropagation()}>
                            <button className="cs-menuitem" onClick={() => startRename(c)}><Pencil size={14} /> Rename</button>
                            <button className="cs-menuitem" style={{ color: '#c0392b' }} onClick={() => confirmDelete(c)}><Trash2 size={14} /> Delete</button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
